import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { AttendancePanel } from '../components/AttendancePanel';
import { GradingStudentSelector } from '../components/GradingStudentSelector';
import {
    ArrowLeft, Users, UserPlus, UserMinus, GraduationCap, Check, X, Save, Award,
    Info, ChevronLeft, ChevronRight, ClipboardCheck, UsersRound, Trash2, Loader2,
    Calendar, MessageSquare, Upload, Download, AlertCircle
} from 'lucide-react';

const LessonDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders, API } = useAuth();

    const [lesson, setLesson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('students');

    // Students & Enrollment
    const [enrolledStudents, setEnrolledStudents] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [enrollModalOpen, setEnrollModalOpen] = useState(false);
    const [enrollSearch, setEnrollSearch] = useState('');

    // Groups
    const [groups, setGroups] = useState([]);
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [editingGroup, setEditingGroup] = useState(null);
    const [uploadingGroups, setUploadingGroups] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    // Attendance
    const [predefinedComments, setPredefinedComments] = useState([]);
    const [panelAttendance, setPanelAttendance] = useState({});
    const [attComments, setAttComments] = useState({});
    const [attPredefined, setAttPredefined] = useState({});
    const [currentAttIdx, setCurrentAttIdx] = useState(0);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
    const [attSaving, setAttSaving] = useState(false);
    const [lessonAttSearch, setLessonAttSearch] = useState('');

    // Navigate to student in specific tab
    const goToStudentTab = (studentId, tab) => {
        const idx = filteredStudents.findIndex(s => s.id === studentId);
        if (tab === 'attendance') {
            setActiveTab('attendance');
            if (idx >= 0) setCurrentAttIdx(idx);
        } else if (tab === 'grading') {
            setActiveTab('grading');
            const gradeIdx = enrolledStudents.findIndex(s => s.id === studentId);
            if (gradeIdx >= 0) setCurrentGradeIdx(gradeIdx);
        }
    };
    // Grading
    const [criteria, setCriteria] = useState([]);
    const [panelGrades, setPanelGrades] = useState({});
    const [currentGradeIdx, setCurrentGradeIdx] = useState(0);
    const [scores, setScores] = useState({});
    const [feedback, setFeedback] = useState('');
    const [gradeSaving, setGradeSaving] = useState(false);
    const [expandedStudentId, setExpandedStudentId] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    const fetchLesson = useCallback(async () => {
        try {
            const [lessonRes, studentsRes, allStudentsRes, groupsRes, attRes, criteriaRes, commentsRes, gradesRes] = await Promise.all([
                axios.get(`${API}/lessons/${id}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/lessons/${id}/enrolled-students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/lessons/${id}/groups`, { headers: getAuthHeaders() }),
                axios.get(`${API}/attendance/${id}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/criteria`, { headers: getAuthHeaders() }),
                axios.get(`${API}/admin/attendance-comments`, { headers: getAuthHeaders() }),
                axios.get(`${API}/grades/lesson/${id}`, { headers: getAuthHeaders() })
            ]);
            const l = lessonRes.data;
            setLesson(l);
            setEnrolledStudents(studentsRes.data);
            setAllStudents(allStudentsRes.data);
            setGroups(groupsRes.data);
            setPredefinedComments(commentsRes.data);

            const lessonCriteria = criteriaRes.data.filter(c => l.criteria_ids?.includes(c.id));
            setCriteria(lessonCriteria);

            // Build attendance maps
            const attMap = {}, commentMap = {}, predMap = {};
            studentsRes.data.forEach(s => { attMap[s.id] = false; commentMap[s.id] = ''; predMap[s.id] = []; });
            attRes.data.forEach(r => { attMap[r.student_id] = r.present; commentMap[r.student_id] = r.comment || ''; predMap[r.student_id] = r.predefined_comments || []; });
            setPanelAttendance(attMap);
            setAttComments(commentMap);
            setAttPredefined(predMap);

            // Build grades map
            const gMap = {};
            gradesRes.data.forEach(g => { gMap[g.student_id] = g; });
            setPanelGrades(gMap);

            if (studentsRes.data.length > 0) loadGrade(studentsRes.data[0].id, gMap);
        } catch (e) {
            toast.error('Failed to load lesson');
            navigate('/lessons');
        } finally { setLoading(false); }
    }, [id, API, getAuthHeaders, navigate]);

    useEffect(() => { fetchLesson(); }, [fetchLesson]);

    const loadGrade = (sid, gMap) => {
        const existing = (gMap || panelGrades)[sid];
        if (existing) {
            const sMap = {};
            existing.scores?.forEach(s => { sMap[s.criterion_id] = s.score; });
            setScores(sMap);
            setFeedback(existing.feedback || '');
        } else { setScores({}); setFeedback(''); }
        setIsDirty(false);
    };

    // Enrollment
    const enrolledIds = enrolledStudents.map(s => s.id);
    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const surnameA = (a.name || '').split(' ').pop().toLowerCase();
        const surnameB = (b.name || '').split(' ').pop().toLowerCase();
        return surnameA.localeCompare(surnameB);
    });
    const availableStudents = sortBySurname(allStudents.filter(s => s.year === lesson?.year && !enrolledIds.includes(s.id)));
    const filteredAvailable = enrollSearch
        ? availableStudents.filter(s => s.name.toLowerCase().includes(enrollSearch.toLowerCase()) || s.student_id.toLowerCase().includes(enrollSearch.toLowerCase()))
        : availableStudents;

    const handleEnroll = async (sid) => {
        try {
            await axios.post(`${API}/lessons/${id}/enroll/${sid}`, {}, { headers: getAuthHeaders() });
            const res = await axios.get(`${API}/lessons/${id}/enrolled-students`, { headers: getAuthHeaders() });
            setEnrolledStudents(res.data);
            // Init attendance for new student
            setPanelAttendance(p => ({ ...p, [sid]: false }));
            setAttComments(p => ({ ...p, [sid]: '' }));
            setAttPredefined(p => ({ ...p, [sid]: [] }));
            toast.success('Student enrolled');
        } catch (e) { toast.error('Failed to enroll'); }
    };

    const handleUnenroll = async (sid) => {
        try {
            await axios.delete(`${API}/lessons/${id}/enroll/${sid}`, { headers: getAuthHeaders() });
            const res = await axios.get(`${API}/lessons/${id}/enrolled-students`, { headers: getAuthHeaders() });
            setEnrolledStudents(res.data);
            toast.success('Student removed');
        } catch (e) { toast.error('Failed'); }
    };

    const enrollAll = async () => {
        for (const s of availableStudents) {
            try { await axios.post(`${API}/lessons/${id}/enroll/${s.id}`, {}, { headers: getAuthHeaders() }); } catch (e) { /* skip */ }
        }
        const res = await axios.get(`${API}/lessons/${id}/enrolled-students`, { headers: getAuthHeaders() });
        setEnrolledStudents(res.data);
        res.data.forEach(s => {
            if (!panelAttendance.hasOwnProperty(s.id)) {
                setPanelAttendance(p => ({ ...p, [s.id]: false }));
                setAttComments(p => ({ ...p, [s.id]: '' }));
                setAttPredefined(p => ({ ...p, [s.id]: [] }));
            }
        });
        toast.success(`Enrolled all Year ${lesson.year} students`);
    };

    // Groups
    const createGroup = async () => {
        if (!newGroupName.trim()) return;
        try {
            await axios.post(`${API}/lessons/${id}/groups`, { name: newGroupName.trim(), student_ids: [] }, { headers: getAuthHeaders() });
            const res = await axios.get(`${API}/lessons/${id}/groups`, { headers: getAuthHeaders() });
            setGroups(res.data);
            setNewGroupName('');
            toast.success('Group created');
        } catch (e) { toast.error('Failed'); }
    };

    const toggleStudentInGroup = async (gid, sid) => {
        const g = groups.find(g => g.id === gid);
        if (!g) return;
        const ids = g.student_ids.includes(sid) ? g.student_ids.filter(i => i !== sid) : [...g.student_ids, sid];
        try {
            await axios.put(`${API}/lessons/${id}/groups/${gid}`, { student_ids: ids }, { headers: getAuthHeaders() });
            setGroups(prev => prev.map(g => g.id === gid ? { ...g, student_ids: ids } : g));
        } catch (e) { toast.error('Failed'); }
    };

    const deleteGroup = async (gid) => {
        try {
            await axios.delete(`${API}/lessons/${id}/groups/${gid}`, { headers: getAuthHeaders() });
            setGroups(prev => prev.filter(g => g.id !== gid));
            if (selectedGroupFilter === gid) setSelectedGroupFilter('all');
        } catch (e) { toast.error('Failed'); }
    };

    const handleGroupUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingGroups(true);
        setUploadResult(null);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            const res = await axios.post(`${API}/lessons/${id}/groups/upload`, formDataUpload, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            setUploadResult(res.data);
            toast.success(res.data.message);
            // Refresh data
            const [sRes, gRes] = await Promise.all([
                axios.get(`${API}/lessons/${id}/enrolled-students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/lessons/${id}/groups`, { headers: getAuthHeaders() })
            ]);
            setEnrolledStudents(sRes.data);
            setGroups(gRes.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally { setUploadingGroups(false); e.target.value = ''; }
    };

    const downloadTemplate = async () => {
        try {
            const res = await axios.get(`${API}/groups/upload-template`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', 'group_upload_template.xlsx');
            document.body.appendChild(link); link.click(); link.remove();
        } catch (e) { toast.error('Failed to download template'); }
    };

    // Attendance
    const filteredStudents = sortBySurname((selectedGroupFilter === 'all'
        ? enrolledStudents
        : enrolledStudents.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids.includes(s.id))
    ).filter(s => !lessonAttSearch || s.name.toLowerCase().includes(lessonAttSearch.toLowerCase()) || s.student_id.toLowerCase().includes(lessonAttSearch.toLowerCase())));

    const currentAttStudent = filteredStudents[currentAttIdx];
    const presentCount = Object.values(panelAttendance).filter(Boolean).length;

    const togglePredefined = (sid, label) => {
        setAttPredefined(prev => {
            const c = prev[sid] || [];
            return { ...prev, [sid]: c.includes(label) ? c.filter(l => l !== label) : [...c, label] };
        });
    };

    const saveAttendance = async () => {
        setAttSaving(true);
        try {
            const records = enrolledStudents.map(s => ({
                student_id: s.id, present: panelAttendance[s.id] || false,
                comment: attComments[s.id] || '', predefined_comments: attPredefined[s.id] || []
            }));
            await axios.post(`${API}/attendance`, { lesson_id: id, records }, { headers: getAuthHeaders() });
            toast.success('Attendance saved');
        } catch (e) { toast.error('Failed'); }
        finally { setAttSaving(false); }
    };

    // Grading - filtered by group
    const gradeFilteredStudents = sortBySurname(selectedGroupFilter === 'all'
        ? enrolledStudents
        : enrolledStudents.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids?.includes(s.id)));
    const currentGradeStudent = enrolledStudents[currentGradeIdx];
    const currentGradeFilteredIdx = currentGradeStudent ? gradeFilteredStudents.findIndex(s => s.id === currentGradeStudent.id) : -1;
    const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
    const maxScore = criteria.length * 5;

    const navigateGradeStudent = async (dir) => {
        const newFilteredIdx = currentGradeFilteredIdx + dir;
        if (newFilteredIdx >= 0 && newFilteredIdx < gradeFilteredStudents.length) {
            // Auto-save current student if dirty
            if (isDirty && currentGradeStudent) {
                await autoSaveCurrentGrade();
            }
            const newStudent = gradeFilteredStudents[newFilteredIdx];
            const newGlobalIdx = enrolledStudents.findIndex(s => s.id === newStudent.id);
            setCurrentGradeIdx(newGlobalIdx);
            loadGrade(newStudent.id, panelGrades);
        }
    };

    const getRatingDescription = (criterion, score) => criterion.rating_descriptions?.find(rd => rd.score === score);

    const getScoreColor = (score) => {
        const map = { 0: 'bg-gray-100 border-gray-200 text-gray-500', 1: 'bg-red-100 border-red-300 text-red-700', 2: 'bg-orange-100 border-orange-300 text-orange-700', 3: 'bg-yellow-100 border-yellow-300 text-yellow-700', 4: 'bg-blue-100 border-blue-300 text-blue-700', 5: 'bg-green-100 border-green-300 text-green-700' };
        return map[score] || map[0];
    };
    const getSelectedScoreColor = (score) => {
        const map = { 0: 'bg-gray-500 border-gray-500 text-white', 1: 'bg-red-500 border-red-500 text-white', 2: 'bg-orange-500 border-orange-500 text-white', 3: 'bg-yellow-500 border-yellow-500 text-white', 4: 'bg-blue-500 border-blue-500 text-white', 5: 'bg-green-500 border-green-500 text-white' };
        return map[score] || map[0];
    };

    const autoSaveCurrentGrade = async () => {
        if (!currentGradeStudent) return;
        try {
            const scoresList = criteria.map(c => ({ criterion_id: c.id, score: scores[c.id] !== undefined ? scores[c.id] : 0 }));
            await axios.post(`${API}/grades`, {
                lesson_id: id, student_id: currentGradeStudent.id, scores: scoresList, feedback
            }, { headers: getAuthHeaders() });
            const total = scoresList.reduce((s, x) => s + x.score, 0);
            setPanelGrades(prev => ({ ...prev, [currentGradeStudent.id]: { scores: scoresList, feedback, total_score: total } }));
            setIsDirty(false);
        } catch (e) { console.error('Auto-save failed', e); }
    };

    const saveGrade = async () => {
        if (!currentGradeStudent) return;
        setGradeSaving(true);
        try {
            const scoresList = criteria.map(c => ({ criterion_id: c.id, score: scores[c.id] !== undefined ? scores[c.id] : 0 }));
            await axios.post(`${API}/grades`, {
                lesson_id: id, student_id: currentGradeStudent.id, scores: scoresList, feedback
            }, { headers: getAuthHeaders() });
            const total = scoresList.reduce((s, x) => s + x.score, 0);
            setPanelGrades(prev => ({ ...prev, [currentGradeStudent.id]: { scores: scoresList, feedback, total_score: total } }));
            setIsDirty(false);
            toast.success('Grade saved');
        } catch (e) { toast.error('Failed'); }
        finally { setGradeSaving(false); }
    };

    const components = lesson?.components || ['attendance', 'grading'];
    const tabs = [
        { id: 'students', label: 'Students', icon: Users },
        ...(components.includes('attendance') ? [{ id: 'attendance', label: 'Attendance', icon: ClipboardCheck }] : []),
        ...(components.includes('grading') ? [{ id: 'grading', label: 'Grading', icon: Award }] : []),
    ];

    if (loading) return (
        <Layout>
            <div className="space-y-4">
                <div className="skeleton h-5 w-48 rounded"></div>
                <div className="skeleton h-8 w-80 rounded"></div>
                <div className="flex gap-3 mt-4">{[1,2,3].map(i => <div key={i} className="skeleton h-10 w-28 rounded-lg" />)}</div>
                <div className="skeleton h-48 rounded-xl mt-4"></div>
            </div>
        </Layout>
    );

    if (!lesson) return (
        <Layout><div className="text-center py-10"><p className="text-gray-500">Lesson not found</p><Button onClick={() => navigate('/lessons')} className="mt-4">Back to Lessons</Button></div></Layout>
    );

    return (
        <Layout>
            <TooltipProvider>
                <div className="space-y-6 page-enter" data-testid="lesson-detail-page">
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-1.5 text-sm text-gray-400" data-testid="breadcrumb">
                        <button onClick={() => navigate('/lessons')} className="hover:text-ica-navy transition-colors">Lessons</button>
                        <span>/</span>
                        <span className="text-gray-700 font-medium truncate max-w-[200px]">{lesson.title}</span>
                    </nav>
                    {/* Header */}
                    <div className="flex items-start gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/lessons')} className="mt-1 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold" data-testid="back-to-lessons" title="Back to Lessons">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div className="flex-1">
                            <h1 className="font-heading text-3xl font-bold text-ica-navy">{lesson.title}</h1>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-gray-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{lesson.date}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-ica-navy/10 text-ica-navy font-medium">Year {lesson.year}</span>
                                <span className="text-sm text-gray-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{enrolledStudents.length} students</span>
                                {components.map(c => (
                                    <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-ica-gold/10 text-ica-gold-dark font-medium">
                                        {c === 'attendance' ? 'Attendance' : 'Grading'}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} testIdPrefix="detail-tab" />

                    {/* Tab Content */}
                    {activeTab === 'students' && (
                        <div className="space-y-6" data-testid="students-tab">
                            {/* Quick actions */}
                            <div className="flex items-center gap-3">
                                <Button onClick={() => setEnrollModalOpen(true)} data-testid="open-enroll-modal">
                                    <UserPlus className="w-4 h-4 mr-2" /> Add Students
                                </Button>
                                {availableStudents.length > 0 && (
                                    <Button variant="outline" onClick={enrollAll} data-testid="enroll-all-btn">
                                        <Users className="w-4 h-4 mr-2" /> Add All Year {lesson.year} ({availableStudents.length})
                                    </Button>
                                )}
                                <Button variant="outline" onClick={() => setGroupModalOpen(true)} data-testid="manage-groups-btn">
                                    <UsersRound className="w-4 h-4 mr-2" /> Manage Groups
                                </Button>
                            </div>

                            {/* Groups overview */}
                            {groups.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="text-sm font-semibold text-gray-700">Groups</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {groups.map(g => (
                                            <div key={g.id} onClick={() => setSelectedGroupFilter(prev => prev === g.id ? 'all' : g.id)}
                                                className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-md ${selectedGroupFilter === g.id ? 'border-ica-gold shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                                                data-testid={`group-card-${g.id}`}>
                                                <div className="flex items-center justify-between mb-3">
                                                    <p className="font-medium text-gray-900 text-sm">{g.name}</p>
                                                    <span className="text-xs text-gray-400">{(g.student_ids || []).filter(sid => enrolledStudents.some(s => s.id === sid)).length} students</span>
                                                </div>
                                                <div className="flex gap-1 flex-wrap">
                                                    {enrolledStudents.filter(s => g.student_ids.includes(s.id)).map(s => (
                                                        <span key={s.id} className="px-2 py-0.5 rounded-full text-xs bg-ica-gold/10 text-ica-gold-dark font-medium">{s.name.split(' ')[0]}</span>
                                                    ))}
                                                    {g.student_ids.length === 0 && <span className="text-xs text-gray-400">No students assigned</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Enrolled students list */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-700">Enrolled Students ({enrolledStudents.length})</h3>
                                    {groups.length > 0 && (
                                        <div className="flex gap-1.5 flex-wrap">
                                            <button onClick={() => setSelectedGroupFilter('all')}
                                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                data-testid="students-group-filter-all">
                                                All ({enrolledStudents.length})
                                            </button>
                                            {groups.map(g => (
                                                <button key={g.id} onClick={() => setSelectedGroupFilter(g.id)}
                                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                    data-testid={`students-group-filter-${g.id}`}>
                                                    {g.name} ({(g.student_ids || []).filter(sid => enrolledStudents.some(s => s.id === sid)).length})
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {(() => {
                                    const studentsTabFiltered = sortBySurname(selectedGroupFilter === 'all'
                                        ? enrolledStudents
                                        : enrolledStudents.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids.includes(s.id)));
                                    return studentsTabFiltered.length > 0 ? (
                                    <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-50">
                                        {studentsTabFiltered.map(s => (
                                            <div key={s.id} data-testid={`enrolled-${s.id}`}>
                                                <div className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/80 transition-colors cursor-pointer"
                                                    onClick={() => setExpandedStudentId(prev => prev === s.id ? null : s.id)}>
                                                    <div className="flex items-center gap-3 flex-1">
                                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${expandedStudentId === s.id ? 'bg-ica-gold/20' : 'bg-ica-navy/10'}`}>
                                                            <GraduationCap className={`w-4 h-4 ${expandedStudentId === s.id ? 'text-ica-gold-dark' : 'text-ica-navy'}`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{s.name}</p>
                                                            <p className="text-xs text-gray-400">{s.student_id}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {groups.filter(g => (g.student_ids || []).includes(s.id)).map(g => (
                                                            <span key={g.id} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{g.name}</span>
                                                        ))}
                                                        <button onClick={(e) => { e.stopPropagation(); goToStudentTab(s.id, 'attendance'); }}
                                                            className="p-1.5 rounded-lg text-slate-500 hover:text-ica-navy hover:bg-gray-100 dark:text-slate-400 dark:hover:text-ica-gold dark:hover:bg-slate-700 active:scale-90 transition-all"
                                                            title="View Attendance" data-testid={`goto-attendance-${s.id}`}>
                                                            <ClipboardCheck className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={(e) => { e.stopPropagation(); goToStudentTab(s.id, 'grading'); }}
                                                            className="p-1.5 rounded-lg text-slate-500 hover:text-ica-navy hover:bg-gray-100 dark:text-slate-400 dark:hover:text-ica-gold dark:hover:bg-slate-700 active:scale-90 transition-all"
                                                            title="View Grading" data-testid={`goto-grading-${s.id}`}>
                                                            <Award className="w-4 h-4" />
                                                        </button>
                                                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleUnenroll(s.id); }} className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" data-testid={`unenroll-${s.id}`} title="Remove Student">
                                                            <UserMinus className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                {expandedStudentId === s.id && (
                                                    <div className="px-5 pb-3 pt-0 flex gap-2 ml-12 animate-in slide-in-from-top-1 duration-150">
                                                        <button onClick={() => goToStudentTab(s.id, 'attendance')}
                                                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 active:scale-95 transition-all"
                                                            data-testid={`expand-attendance-${s.id}`}>
                                                            <ClipboardCheck className="w-4 h-4" /> View Attendance
                                                        </button>
                                                        <button onClick={() => goToStudentTab(s.id, 'grading')}
                                                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 active:scale-95 transition-all"
                                                            data-testid={`expand-grading-${s.id}`}>
                                                            <Award className="w-4 h-4" /> View Grading
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                                        <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                        <p className="text-gray-500 font-medium">No students enrolled</p>
                                        <p className="text-sm text-gray-400 mt-1">Click "Add Students" to enrol students</p>
                                    </div>
                                );
                                })()}
                            </div>
                        </div>
                    )}

                    {activeTab === 'attendance' && (
                        <AttendancePanel
                            students={enrolledStudents}
                            groups={groups}
                            groupFilter={selectedGroupFilter}
                            onGroupFilter={setSelectedGroupFilter}
                            search={lessonAttSearch}
                            onSearch={setLessonAttSearch}
                            predefinedComments={predefinedComments}
                            isPresent={(sid) => panelAttendance[sid]}
                            onSetPresent={(sid, val) => setPanelAttendance(p => ({ ...p, [sid]: val }))}
                            predefinedFor={(sid) => attPredefined[sid] || []}
                            onTogglePredefined={(sid, label) => togglePredefined(sid, label)}
                            commentFor={(sid) => attComments[sid] || ''}
                            onSetComment={(sid, val) => setAttComments(p => ({ ...p, [sid]: val }))}
                            onSave={saveAttendance}
                            saving={attSaving}
                        />
                    )}

                    {activeTab === 'grading' && (
                        <div className="space-y-4" data-testid="grading-tab">
                            {enrolledStudents.length === 0 ? (
                                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                                    <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                    <p className="text-gray-500">No students enrolled yet</p>
                                </div>
                            ) : criteria.length === 0 ? (
                                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                                    <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                    <p className="text-gray-500">No criteria assigned to this lesson</p>
                                    <p className="text-sm text-gray-400 mt-1">Edit the lesson and select grading criteria</p>
                                </div>
                            ) : (
                                <>
                                    <GradingStudentSelector
                                        students={enrolledStudents}
                                        groups={groups}
                                        groupFilter={selectedGroupFilter}
                                        onGroupFilter={(gf) => { setSelectedGroupFilter(gf); setCurrentGradeIdx(0); }}
                                        currentStudentId={currentGradeStudent?.id}
                                        onSelectStudent={async (sid) => { if (isDirty && currentGradeStudent) { await autoSaveCurrentGrade(); } const gi = enrolledStudents.findIndex(st => st.id === sid); setCurrentGradeIdx(gi); loadGrade(sid, panelGrades); }}
                                        gradedStudentIds={Object.keys(panelGrades)}
                                        rightSlot={<div className="text-right"><p className="text-2xl font-bold text-ica-gold">{totalScore}<span className="text-gray-400 text-lg"> / {maxScore}</span></p></div>}
                                        statusSlot={
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    {isDirty && <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />}
                                                    <span className="text-xs text-gray-400">{isDirty ? 'Unsaved changes — auto-saves on navigate' : 'All changes saved'}</span>
                                                </div>
                                                <Button onClick={saveGrade} disabled={gradeSaving} className="bg-ica-gold hover:bg-[#a8803a]" data-testid="save-grade-btn">
                                                    {gradeSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Grade</>}
                                                </Button>
                                            </div>
                                        }
                                    />

                                    {/* Criteria scoring */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-6">
                                        {criteria.map(criterion => {
                                            const selectedScore = scores[criterion.id];
                                            const selectedRating = getRatingDescription(criterion, selectedScore);
                                            return (
                                                <div key={criterion.id} data-testid={`criterion-${criterion.id}`}>
                                                    <div className="flex items-start justify-between mb-2">
                                                        <p className="text-sm font-medium text-gray-900">{criterion.name}</p>
                                                        <span className="text-xs font-medium text-ica-gold ml-2">{selectedScore !== undefined ? selectedScore : '-'}/5</span>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {[0, 1, 2, 3, 4, 5].map(score => {
                                                            const rating = getRatingDescription(criterion, score);
                                                            const isSelected = selectedScore === score;
                                                            return (
                                                                <Tooltip key={score}>
                                                                    <TooltipTrigger asChild>
                                                                        <button onClick={() => { setScores(p => { const newScores = { ...p }; if (newScores[criterion.id] === score) { delete newScores[criterion.id]; } else { newScores[criterion.id] = score; } return newScores; }); setIsDirty(true); }}
                                                                            className={`w-11 h-11 rounded-lg font-bold text-sm transition-all border-2 ${isSelected ? getSelectedScoreColor(score) + ' shadow-md scale-105' : getScoreColor(score) + ' hover:scale-105'}`}>
                                                                            {score}
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    {rating && <TooltipContent side="top" className="max-w-xs"><p className="font-semibold">{rating.label}</p><p className="text-sm text-gray-300">{rating.description}</p></TooltipContent>}
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </div>
                                                    {selectedRating && (
                                                        <div className="flex items-start gap-2 p-2.5 bg-ica-gold/5 border border-ica-gold/20 rounded-lg mt-2">
                                                            <Info className="w-3.5 h-3.5 text-ica-gold mt-0.5 flex-shrink-0" />
                                                            <div><p className="text-xs font-medium text-ica-gold-dark">{selectedRating.label}</p><p className="text-xs text-gray-600">{selectedRating.description}</p></div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Feedback</label>
                                            <Textarea value={feedback} onChange={e => { setFeedback(e.target.value); setIsDirty(true); }} placeholder="Additional comments..." rows={3} className="resize-none text-sm" data-testid="grade-feedback" />
                                        </div>
                                        <Button onClick={saveGrade} disabled={gradeSaving} className="w-full bg-ica-gold hover:bg-[#a8803a]" data-testid="save-grade-btn">
                                            {gradeSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Grade</>}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Enroll Modal */}
                    <Dialog open={enrollModalOpen} onOpenChange={setEnrollModalOpen}>
                        <DialogContent className="max-w-lg" data-testid="enroll-modal">
                            <DialogHeader><DialogTitle>Add Students to {lesson.title}</DialogTitle></DialogHeader>
                            <div className="space-y-4 py-2">
                                <Input value={enrollSearch} onChange={e => setEnrollSearch(e.target.value)} placeholder="Search students..." data-testid="enroll-search" />
                                {availableStudents.length > 0 && (
                                    <Button variant="outline" className="w-full" onClick={() => { enrollAll(); setEnrollModalOpen(false); }} data-testid="enroll-all-modal-btn">
                                        <Users className="w-4 h-4 mr-2" /> Add All Year {lesson.year} Students ({availableStudents.length})
                                    </Button>
                                )}
                                <div className="max-h-64 overflow-y-auto space-y-1.5">
                                    {filteredAvailable.length > 0 ? filteredAvailable.map(s => (
                                        <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div>
                                                <p className="text-sm font-medium">{s.name}</p>
                                                <p className="text-xs text-gray-500">{s.student_id}</p>
                                            </div>
                                            <Button size="sm" onClick={() => handleEnroll(s.id)} className="bg-green-600 hover:bg-green-700 h-8" data-testid={`enroll-${s.id}`}>
                                                <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
                                            </Button>
                                        </div>
                                    )) : (
                                        <p className="text-center text-gray-400 py-6 text-sm">
                                            {enrollSearch ? 'No matching students' : `All Year ${lesson.year} students are already enrolled`}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Groups Modal */}
                    <Dialog open={groupModalOpen} onOpenChange={(open) => { setGroupModalOpen(open); if (!open) setUploadResult(null); }}>
                        <DialogContent className="max-w-md" data-testid="groups-modal">
                            <DialogHeader><DialogTitle>Manage Groups</DialogTitle></DialogHeader>
                            <div className="space-y-4 py-2">
                                {/* Upload section */}
                                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Upload Groups</p>
                                    <p className="text-xs text-gray-400">Upload XLSX/CSV with columns: Group, Student Number / Name / Email</p>
                                    <div className="flex gap-2">
                                        <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed cursor-pointer transition-all ${uploadingGroups ? 'border-gray-200 bg-gray-100' : 'border-gray-300 hover:border-ica-gold hover:bg-ica-gold/5'}`}>
                                            {uploadingGroups ? <><Loader2 className="w-4 h-4 animate-spin text-gray-400" /><span className="text-sm text-gray-400">Uploading...</span></> : <><Upload className="w-4 h-4 text-gray-500" /><span className="text-sm text-gray-600">Choose File</span></>}
                                            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleGroupUpload} className="hidden" disabled={uploadingGroups} data-testid="group-upload-input" />
                                        </label>
                                        <Button variant="outline" size="sm" onClick={downloadTemplate} className="h-auto" data-testid="download-template-btn">
                                            <Download className="w-4 h-4 mr-1" /> Template
                                        </Button>
                                    </div>
                                    {uploadResult && (
                                        <div className="text-xs space-y-1 p-2 bg-white rounded-lg border border-gray-100">
                                            <p className="text-green-600 font-medium">{uploadResult.groups_created} created, {uploadResult.groups_updated} updated, {uploadResult.students_enrolled} enrolled</p>
                                            {uploadResult.students_not_found?.length > 0 && (
                                                <div className="flex items-start gap-1 text-amber-600">
                                                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                                    <span>Not found: {uploadResult.students_not_found.join(', ')}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Manual create */}
                                <div className="flex gap-2">
                                    <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="New group name..." className="flex-1" data-testid="new-group-input"
                                        onKeyDown={e => { if (e.key === 'Enter') createGroup(); }} />
                                    <Button onClick={createGroup} data-testid="create-group-btn">Add</Button>
                                </div>
                                {groups.map(g => (
                                    <div key={g.id} className="border border-gray-100 rounded-xl p-4" data-testid={`group-edit-${g.id}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-sm font-medium text-gray-900">{g.name}</p>
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300" onClick={() => deleteGroup(g.id)} title="Delete Group">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                        <div className="flex gap-1.5 flex-wrap">
                                            {enrolledStudents.map(s => (
                                                <button key={s.id} onClick={() => toggleStudentInGroup(g.id, s.id)}
                                                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${g.student_ids.includes(s.id) ? 'bg-ica-gold text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                                    {s.name.split(' ')[0]}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                {groups.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No groups yet</p>}
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </TooltipProvider>
        </Layout>
    );
};

export default LessonDetailPage;
;
